import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info, MoreVertical, Printer, Settings, X } from "lucide-react";
import { toast } from "react-hot-toast";
import { printAllPoliciesAsDocument, printSinglePolicyAsDocument } from "./policySummaryPrint";
import {
  buildSeparationPolicyDocumentHtml,
  clonePolicySnapshot,
  computeVersionOnSave,
  getPolicySnapshotsForDisplay,
  mergePolicySnapshots,
  printSeparationPolicyDocumentHtml,
} from "./separationPolicyDocument";
import {
  MASTERS_DOCUMENT_COMPANY_ADDRESS,
  MASTERS_DOCUMENT_COMPANY_NAME,
} from "./masters/companyDocumentDefaults";
import { employeeService, separationPolicyService } from "../../services/api";

const NOTICE_PERIOD_TYPES = [
  { value: "calendar", label: "Calendar days" },
  { value: "working", label: "Working days (by attendance)" },
];
/**
 * Attendance / leave status codes that may count as a working day toward notice (align with leave policy).
 * Order: PR, WO, PF, EL, CL, SL first, then other common codes.
 */
const NOTICE_PERIOD_STATUS_CODES = [
  { value: "PR", label: "PR" },
  { value: "WO", label: "WO" },
  { value: "PF", label: "PF" },
  { value: "EL", label: "EL" },
  { value: "CL", label: "CL" },
  { value: "SL", label: "SL" },
  { value: "CO", label: "CO" },
  { value: "PH", label: "PH" },
  { value: "ML", label: "ML" },
  { value: "PL", label: "PL" },
  { value: "OD", label: "OD" },
  { value: "WFH", label: "WFH" },
  { value: "HD", label: "HD" },
];
const NOTICE_PERIOD_CODE_SET = new Set(NOTICE_PERIOD_STATUS_CODES.map((x) => x.value));

/** Legacy names — same list as {@link NOTICE_PERIOD_STATUS_CODES} (avoids runtime errors if an old reference remains). */
// eslint-disable-next-line no-unused-vars -- kept for stale HMR / any remaining JSX reference to old constant names
const ATTENDANCE_STATUSES_FOR_NOTICE = NOTICE_PERIOD_STATUS_CODES;
// eslint-disable-next-line no-unused-vars -- kept for stale HMR / any remaining JSX reference to old constant names
const LEAVE_CODES_FOR_NOTICE = NOTICE_PERIOD_STATUS_CODES;

/** Prefer leaveCodes*; else migrate known codes from legacy attendance arrays (in order). */
function mergeNoticePeriodCodes(leaveArr, legacyA, legacyB, fallback) {
  if (Array.isArray(leaveArr) && leaveArr.length) return [...leaveArr];
  for (const legacy of [legacyA, legacyB]) {
    if (!Array.isArray(legacy) || !legacy.length) continue;
    const fromLegacy = legacy.filter((c) => NOTICE_PERIOD_CODE_SET.has(String(c).toUpperCase()));
    if (fromLegacy.length) return fromLegacy;
  }
  return Array.isArray(fallback) ? [...fallback] : [];
}

/** One list for working-day notice: prefer leaveCodesForNotice; else union probation + confirmed (legacy) in standard order. */
function mergeSharedNoticePeriodCodes(row, d) {
  if (Array.isArray(row.leaveCodesForNotice) && row.leaveCodesForNotice.length) {
    return [...row.leaveCodesForNotice];
  }
  const prob = mergeNoticePeriodCodes(
    row.leaveCodesProbation,
    row.attendanceStatusesProbation,
    row.attendanceStatusesForNotice,
    d.leaveCodesProbation
  );
  const conf = mergeNoticePeriodCodes(
    row.leaveCodesConfirmed,
    row.attendanceStatusesConfirmed,
    undefined,
    d.leaveCodesConfirmed
  );
  const set = new Set([...prob, ...conf]);
  if (set.size === 0) return [];
  return NOTICE_PERIOD_STATUS_CODES.map((x) => x.value).filter((v) => set.has(v));
}

const FEEDBACK_TEMPLATE_OPTIONS = [
  { value: "", label: "Select feedback template" },
  { value: "standard_exit", label: "Standard Exit Interview" },
  { value: "detailed_exit", label: "Detailed Exit Questionnaire" },
  { value: "custom_1", label: "Custom Template 1" },
  { value: "custom_2", label: "Custom Template 2" },
];
const LETTER_RESIGNATION_OPTIONS = [
  { value: "", label: "Select template" },
  { value: "standard", label: "Standard Relieving Letter" },
  { value: "acceptance", label: "Resignation Acceptance Letter" },
  { value: "custom_1", label: "Custom Template 1" },
];
const LETTER_TERMINATION_OPTIONS = [
  { value: "", label: "Select template" },
  { value: "standard", label: "Standard Termination Letter" },
  { value: "exit", label: "Exit / Full & Final Letter" },
  { value: "custom_1", label: "Custom Template 1" },
];
const CHECKLIST_TRIGGER_EVENTS = [
  { value: "resignation_approval", label: "Resignation approval" },
  { value: "resignation_cancellation", label: "Resignation cancellation" },
  { value: "termination_approval", label: "Termination approval" },
  { value: "termination_cancellation", label: "Termination cancellation" },
];
const CHECKLIST_DEPARTMENTS = [
  { value: "", label: "Select department" },
  { value: "hr", label: "HR" },
  { value: "it", label: "IT" },
  { value: "admin", label: "Admin" },
  { value: "finance", label: "Finance" },
];
const CHECKLIST_ACTION_ROLES = [
  { value: "manager", label: "Manager" },
  { value: "hr", label: "HR" },
  { value: "management", label: "Management" },
  { value: "reporting_manager", label: "Reporting Manager" },
  { value: "it", label: "IT" },
  { value: "admin", label: "Admin" },
  { value: "finance", label: "Finance" },
];
const DUE_DATE_LOGIC_OPTIONS = [
  { value: "approval_date", label: "From approval date" },
  { value: "fixed_days", label: "Fixed days from trigger" },
];
const LOCATION_OPTIONS = [
  { value: "", label: "All Locations" },
  { value: "1", label: "Mumbai" },
  { value: "2", label: "Delhi" },
  { value: "3", label: "Bangalore" },
];
const EMPLOYMENT_TYPE_OPTIONS = [
  { value: "full_time", label: "Full-time" },
  { value: "contract", label: "Contract" },
  { value: "intern", label: "Intern" },
  { value: "consultant", label: "Consultant" },
  { value: "external", label: "External" },
  { value: "part_time", label: "Part-time" },
  { value: "trainee", label: "Trainee" },
  { value: "full-time", label: "Full-time (legacy)" },
];
const ORG_APPLICABILITY_KEYS = [
  "branch",
  "department",
  "designation",
  "grade",
  "category",
  "subDepartment",
  "location",
];
const WORKFLOW_ROLES = [
  { value: "manager", label: "Manager" },
  { value: "hr", label: "HR" },
  { value: "management", label: "Management" },
  { value: "reporting_manager", label: "Reporting Manager" },
];
const APPROVAL_ROLES = [
  { value: "ceo", label: "CEO" },
  { value: "management", label: "Management" },
  { value: "hr", label: "HR" },
  { value: "manager", label: "Manager" },
  { value: "reporting_manager", label: "Reporting Manager" },
];

function newTaskId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `task-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeChecklistItem(item = {}) {
  return {
    taskId: item.taskId || newTaskId(),
    triggerEvent: item.triggerEvent || "resignation_approval",
    department: item.department || "",
    actionRole: item.actionRole || "hr",
    name: item.name || "",
    mandatory: typeof item.mandatory === "boolean" ? item.mandatory : true,
    dueDateLogic: item.dueDateLogic || "approval_date",
    dueDateDays: typeof item.dueDateDays === "number" ? item.dueDateDays : 7,
    dependsOnTaskId: item.dependsOnTaskId || "",
  };
}

function normalizeWorkflowStep(step = {}) {
  return {
    role: step.role || "manager",
    slaDays: typeof step.slaDays === "number" ? step.slaDays : 3,
    notifyOnly: !!step.notifyOnly,
    noticeWaiverAllowed: !!step.noticeWaiverAllowed,
    noticeWaiverMaxDays:
      typeof step.noticeWaiverMaxDays === "number" ? step.noticeWaiverMaxDays : 0,
  };
}

function createDefaultPolicy() {
  return {
    policyName: "",
    effectiveDate: new Date().toISOString().slice(0, 10),
    autoAssignOnJoin: false,
    allEmployees: false,
    branch: "",
    department: "",
    designation: "",
    grade: "",
    category: "",
    subDepartment: "",
    location: "",
    individualEmployeeIds: "",
    employmentTypes: [],
    approvalMethod: "auto_approved",
    reminderDays: 7,
    needWorkflowSteps: false,
    autoLetter: "no",
    letterFormat: "",
    letterResignation: "",
    letterResignationAcceptance: "",
    letterCancellation: "",
    letterTermination: "",
    useCancellationLetter: false,
    cancellationLetterFormat: "",
    autoLetterTermination: "no",
    workflowParallelApproval: false,
    workflowEscalationAfterDays: 3,
    workflowAutoApprovalIfNoAction: false,
    workflowRejectionWithReason: true,
    workflowReinitiationAllowed: true,
    noticePeriodType: "calendar",
    defaultNoticePeriodDays: 30,
    attendanceStatusesForNotice: [],
    leaveCodesForNotice: [],
    noticeProbationType: "calendar",
    defaultNoticeProbationDays: 30,
    attendanceStatusesProbation: [],
    leaveCodesProbation: [],
    noticeConfirmedType: "calendar",
    defaultNoticeConfirmedDays: 30,
    attendanceStatusesConfirmed: [],
    leaveCodesConfirmed: [],
    exitInterviewMandatory: "no",
    exitInterviewFeedbackTemplate: "",
    allowBackdatedResignation: false,
    backdatedProofMandatory: true,
    workflowSteps: [],
    terminationWorkflowSteps: [],
    cancellationWorkflowSteps: [],
    checklistMappings: [],
    alerts: { hr: true, manager: true, employee: true },
    policyDocumentPushed: false,
    policyPushedAt: null,
    policyVersion: 1,
    policyVersionHistory: [],
    policySnapshots: [],
  };
}

function normalizePolicyRow(row) {
  const d = createDefaultPolicy();
  const at = row.assignedTo || {};
  const policyName = row.policyName || row.name || "";
  const dept =
    row.department != null && row.department !== ""
      ? String(row.department)
      : at.departments?.[0] != null
        ? String(at.departments[0])
        : "";
  return {
    ...d,
    ...row,
    policyName,
    allEmployees: !!row.allEmployees || !!at.allEmployees,
    department: dept,
    employmentTypes:
      Array.isArray(row.employmentTypes) && row.employmentTypes.length
        ? row.employmentTypes
        : Array.isArray(at.employmentTypes)
          ? [...at.employmentTypes]
          : [],
    individualEmployeeIds:
      row.individualEmployeeIds != null && row.individualEmployeeIds !== ""
        ? Array.isArray(row.individualEmployeeIds)
          ? row.individualEmployeeIds.join(", ")
          : String(row.individualEmployeeIds)
        : Array.isArray(at.employees) && at.employees.length
          ? at.employees.join(", ")
          : "",
    workflowSteps: Array.isArray(row.workflowSteps)
      ? row.workflowSteps.map(normalizeWorkflowStep)
      : [],
    terminationWorkflowSteps: Array.isArray(row.terminationWorkflowSteps)
      ? row.terminationWorkflowSteps.map(normalizeWorkflowStep)
      : [],
    cancellationWorkflowSteps: Array.isArray(row.cancellationWorkflowSteps)
      ? row.cancellationWorkflowSteps.map(normalizeWorkflowStep)
      : [],
    checklistMappings: Array.isArray(row.checklistMappings)
      ? row.checklistMappings.map(normalizeChecklistItem)
      : [],
    noticeProbationType: row.noticeProbationType ?? row.noticePeriodType ?? d.noticeProbationType,
    defaultNoticeProbationDays: row.defaultNoticeProbationDays ?? row.defaultNoticePeriodDays ?? d.defaultNoticeProbationDays,
    noticeConfirmedType: row.noticeConfirmedType ?? row.noticePeriodType ?? d.noticeConfirmedType,
    defaultNoticeConfirmedDays: row.defaultNoticeConfirmedDays ?? row.defaultNoticePeriodDays ?? d.defaultNoticeConfirmedDays,
    ...(() => {
      const shared = mergeSharedNoticePeriodCodes(row, d);
      return {
        leaveCodesForNotice: shared,
        attendanceStatusesForNotice: shared,
        leaveCodesProbation: shared,
        leaveCodesConfirmed: shared,
        attendanceStatusesProbation: shared,
        attendanceStatusesConfirmed: shared,
      };
    })(),
    letterResignationAcceptance: row.letterResignationAcceptance ?? d.letterResignationAcceptance,
    exitInterviewMandatory: row.exitInterviewMandatory ?? d.exitInterviewMandatory,
    exitInterviewFeedbackTemplate: row.exitInterviewFeedbackTemplate ?? d.exitInterviewFeedbackTemplate,
    policyVersion: row.policyVersion ?? 1,
    policyVersionHistory: Array.isArray(row.policyVersionHistory) ? row.policyVersionHistory : [],
    policySnapshots: Array.isArray(row.policySnapshots) ? row.policySnapshots : [],
  };
}

function buildAssignedTo(p) {
  const ids = String(p.individualEmployeeIds || "")
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const employees = ids.map((x) => {
    const n = Number(x);
    return Number.isNaN(n) ? x : n;
  });
  if (p.allEmployees) {
    return {
      allEmployees: true,
      departments: [],
      employmentTypes: [],
      employmentStatuses: [],
      employees: [],
    };
  }
  const departments = [];
  if (p.department) {
    const n = Number(p.department);
    departments.push(Number.isNaN(n) ? p.department : n);
  }
  return {
    allEmployees: false,
    departments,
    employmentTypes: Array.isArray(p.employmentTypes) ? [...p.employmentTypes] : [],
    employmentStatuses: [],
    employees,
  };
}

function mergePayloadForApi(form, versionExtras = {}) {
  const policyName = String(form.policyName || "").trim();
  const assignedTo = buildAssignedTo(form);
  const confirmedDays = form.defaultNoticeConfirmedDays ?? form.defaultNoticePeriodDays ?? 30;
  const noticeCodes = Array.isArray(form.leaveCodesForNotice)
    ? form.leaveCodesForNotice
    : Array.isArray(form.leaveCodesProbation) && form.leaveCodesProbation.length
      ? form.leaveCodesProbation
      : Array.isArray(form.leaveCodesConfirmed)
        ? form.leaveCodesConfirmed
        : [];
  const codes = [...noticeCodes];
  return {
    ...form,
    name: policyName || form.name || "",
    policyName,
    noticePeriodDays: form.noticePeriodDays ?? confirmedDays,
    defaultNoticePeriodDays: form.defaultNoticePeriodDays ?? confirmedDays,
    attendanceStatusesProbation: codes,
    attendanceStatusesConfirmed: codes,
    leaveCodesProbation: codes,
    leaveCodesConfirmed: codes,
    leaveCodesForNotice: codes,
    attendanceStatusesForNotice: codes,
    conditionalWaiverRules: [],
    allowResignationsPriorToCurrentDate: false,
    checklistMandatoryForFnf: false,
    assignedTo,
    ...versionExtras,
  };
}

const POLICY_SIGNATURE_OMIT = new Set([
  "id",
  "createdAt",
  "updatedAt",
  "policySnapshots",
  "policyVersionHistory",
  "policyDocumentPushed",
  "policyPushedAt",
  "policyVersion",
  "effectiveDate",
  "status",
]);

function policyContentSignature(p) {
  if (!p) return "";
  const o = {};
  Object.keys(p)
    .sort()
    .forEach((k) => {
      if (POLICY_SIGNATURE_OMIT.has(k)) return;
      let v = p[k];
      if (k === "employmentTypes") v = Array.isArray(v) ? [...v].map(String).sort() : [];
      o[k] = v;
    });
  return JSON.stringify(o);
}

function parseIndividualIds(policy) {
  const s = policy.individualEmployeeIds;
  if (Array.isArray(s)) return s.map(String).filter(Boolean);
  return String(s || "")
    .split(/[\s,]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function normalizeEmploymentTypesArr(policy) {
  const a = policy.employmentTypes;
  if (!Array.isArray(a) || a.length === 0) return null;
  return [...new Set(a.map(String))].sort();
}

function scopesApplicabilityOverlap(a, b) {
  if (String(a.id) === String(b.id)) return false;
  const aIds = parseIndividualIds(a);
  const bIds = parseIndividualIds(b);
  const aAll = !!a.allEmployees;
  const bAll = !!b.allEmployees;
  const aHasOrg = ORG_APPLICABILITY_KEYS.some((k) => a[k]);
  const bHasOrg = ORG_APPLICABILITY_KEYS.some((k) => b[k]);
  if (!aAll && !bAll && !aHasOrg && !bHasOrg && aIds.length && bIds.length) {
    const setB = new Set(bIds);
    return aIds.some((id) => setB.has(id));
  }
  if (aAll || bAll) {
    const aEm = normalizeEmploymentTypesArr(a);
    const bEm = normalizeEmploymentTypesArr(b);
    if (aEm && bEm) {
      const inter = aEm.filter((t) => bEm.includes(t));
      if (inter.length === 0) return false;
    }
    return true;
  }
  for (const k of ORG_APPLICABILITY_KEYS) {
    const va = a[k] ? String(a[k]) : "";
    const vb = b[k] ? String(b[k]) : "";
    if (va && vb && va !== vb) return false;
  }
  const aEm = normalizeEmploymentTypesArr(a);
  const bEm = normalizeEmploymentTypesArr(b);
  if (aEm && bEm) {
    const inter = aEm.filter((t) => bEm.includes(t));
    if (inter.length === 0) return false;
  }
  if (aIds.length && bIds.length) {
    const setB = new Set(bIds);
    return aIds.some((id) => setB.has(id));
  }
  if ((aIds.length && (bHasOrg || bAll)) || (bIds.length && (aHasOrg || aAll))) return true;
  if (aIds.length || bIds.length) return true;
  return true;
}

function findConflictingPolicies(candidate, list, excludeId) {
  return list.filter((p) => {
    if (p.status === "inactive") return false;
    if (excludeId && String(p.id) === String(excludeId)) return false;
    return scopesApplicabilityOverlap(candidate, p);
  });
}

function summarizeOverlapReason(candidate, other) {
  const bits = [];
  if (candidate.allEmployees || other.allEmployees) {
    bits.push(
      'One policy is "All employees" (or whole organization). That overlaps other scopes that still apply to the same people.'
    );
  }
  for (const k of ORG_APPLICABILITY_KEYS) {
    const va = candidate[k] ? String(candidate[k]) : "";
    const vb = other[k] ? String(other[k]) : "";
    if (va && vb && va === vb) {
      bits.push(`Same ${k}: "${va}".`);
    }
  }
  const aEm = normalizeEmploymentTypesArr(candidate);
  const bEm = normalizeEmploymentTypesArr(other);
  if (aEm && bEm) {
    const inter = aEm.filter((t) => bEm.includes(t));
    if (inter.length) {
      bits.push(
        `Overlapping employment type(s): ${inter
          .map((v) => EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v)
          .join(", ")}.`
      );
    }
  }
  if (!bits.length) {
    bits.push("These applicability rules may assign the same employees to more than one active separation policy.");
  }
  return bits.join(" ");
}

function escapeXml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Same visual language as confirmation policy flowchart: START → spine → decision diamond →
 * outcome columns → join → clearance (see buildUnifiedConfirmationProcessSvg).
 */
function buildSeparationFlowchartSvg(policy) {
  const p = policy || {};
  const cx = 460;
  const W = 920;
  const wf = Array.isArray(p.workflowSteps) ? p.workflowSteps : [];
  const tw = Array.isArray(p.terminationWorkflowSteps) ? p.terminationWorkflowSteps : [];
  const cw = Array.isArray(p.cancellationWorkflowSteps) ? p.cancellationWorkflowSteps : [];
  const chk = Array.isArray(p.checklistMappings) ? p.checklistMappings : [];
  const mode =
    p.approvalMethod === "workflow"
      ? "Through workflow"
      : p.approvalMethod === "on_instance"
        ? "On instance"
        : "Auto approved";
  const isWf = p.approvalMethod === "workflow";
  const roleLab = (r) => WORKFLOW_ROLES.find((x) => x.value === r)?.label || r;

  const parts = [];
  let y = 32;
  const pushLine = (y0, y1) => {
    parts.push(
      `<line x1="${cx}" y1="${y0}" x2="${cx}" y2="${y1}" stroke="#64748b" stroke-width="2" marker-end="url(#sep-uflow-arr)"/>`
    );
  };

  parts.push(
    `<text x="${cx}" y="${y}" text-anchor="middle" font-size="15" font-weight="700" fill="#0f172a">Separation process (single flow)</text>`
  );
  y += 22;
  parts.push(
    `<text x="${cx}" y="${y}" text-anchor="middle" font-size="10" fill="#64748b">Notice → approval mode → route → three paths → clearance.</text>`
  );
  y += 28;

  parts.push(`<circle cx="${cx}" cy="${y}" r="14" stroke="#64748b" fill="#fff" stroke-width="2"/>`);
  parts.push(`<text x="${cx}" y="${y + 4}" text-anchor="middle" font-size="9" fill="#334155">START</text>`);
  y += 22;
  pushLine(y - 8, y);

  const probL = `${p.noticeProbationType === "working" ? "Working" : "Calendar"} ${p.defaultNoticeProbationDays ?? p.defaultNoticePeriodDays ?? 30}d`;
  const confL = `${p.noticeConfirmedType === "working" ? "Working" : "Calendar"} ${p.defaultNoticeConfirmedDays ?? p.defaultNoticePeriodDays ?? 30}d`;
  const noticeH = 56;
  parts.push(
    `<rect x="${cx - 130}" y="${y}" width="260" height="${noticeH}" rx="8" fill="#fff" stroke="#334155" stroke-width="1.5"/>`
  );
  parts.push(
    `<text x="${cx}" y="${y + 18}" text-anchor="middle" font-size="10" font-weight="700" fill="#0f172a">Notice (probation vs confirmed)</text>`
  );
  parts.push(
    `<text x="${cx}" y="${y + 34}" text-anchor="middle" font-size="8.5" fill="#475569">Probation: ${escapeXml(probL)} · Confirmed: ${escapeXml(confL)}</text>`
  );
  parts.push(
    `<text x="${cx}" y="${y + 48}" text-anchor="middle" font-size="8" fill="#64748b">Backdated: ${p.allowBackdatedResignation ? "Yes" : "No"} · Exit interview: ${p.exitInterviewMandatory === "yes" ? "Mandatory" : "Optional"}</text>`
  );
  y += noticeH + 14;

  pushLine(y - 14, y);
  parts.push(
    `<rect x="${cx - 125}" y="${y}" width="250" height="38" rx="6" fill="#eef2ff" stroke="#6366f1" stroke-width="1.2"/>`
  );
  parts.push(
    `<text x="${cx}" y="${y + 24}" text-anchor="middle" font-size="9.5" font-weight="600" fill="#312e81">Approval: ${escapeXml(mode)}</text>`
  );
  y += 52;

  pushLine(y - 14, y);
  const d = 26;
  parts.push(
    `<polygon points="${cx},${y} ${cx + d},${y + d} ${cx},${y + 2 * d} ${cx - d},${y + d}" fill="#fff" stroke="#0f172a" stroke-width="2"/>`
  );
  parts.push(
    `<text x="${cx}" y="${y + d + 4}" text-anchor="middle" font-size="8" font-weight="700" fill="#0f172a">Route</text>`
  );
  parts.push(
    `<text x="${cx}" y="${y + 2 * d - 4}" text-anchor="middle" font-size="7" fill="#64748b">Resignation · Termination · Cancellation</text>`
  );
  const decisionBottom = y + 2 * d;
  y = decisionBottom + 18;
  parts.push(
    `<text x="${cx}" y="${y}" text-anchor="middle" font-size="9" font-weight="600" fill="#475569">Then follow the lane for this case (same policy, three paths)</text>`
  );
  y += 22;

  const buildLaneLines = (steps) => {
    const lines = [];
    if (!isWf) {
      lines.push("No standing workflow in policy");
      lines.push("HR processes per approval mode (auto / on instance)");
      lines.push("Letters & checklist still apply");
      return lines;
    }
    if (!steps.length) {
      lines.push("Add workflow steps for this path");
    } else {
      steps.forEach((s, i) => {
        const sub = s.notifyOnly ? "Notify only" : `Approve / SLA ${s.slaDays ?? 3}d`;
        const wv =
          s.noticeWaiverAllowed && Number(s.noticeWaiverMaxDays) > 0
            ? ` · Waiver ≤${s.noticeWaiverMaxDays}d`
            : "";
        lines.push(`Step ${i + 1}: ${roleLab(s.role)} — ${sub}${wv}`);
      });
    }
    lines.push("Relieving / acceptance / termination letters per template");
    lines.push("Run clearance checklist & exit steps");
    return lines;
  };

  const outcomeDefs = [
    { title: "Resignation", color: "#1d4ed8", lines: buildLaneLines(wf) },
    { title: "Termination", color: "#7c3aed", lines: buildLaneLines(tw) },
    { title: "Cancellation", color: "#0d9488", lines: buildLaneLines(cw) },
  ];

  const boxW = 268;
  const gap = 12;
  const lineH = 12;
  const padTop = 22;
  const heights = outcomeDefs.map((o) => padTop + o.lines.length * lineH + 10);
  const boxH = Math.max(...heights);
  const totalW = 3 * boxW + 2 * gap;
  const startX = cx - totalW / 2;
  const rowTop = y;

  const centers = [0, 1, 2].map((i) => startX + i * (boxW + gap) + boxW / 2);
  const colors = ["#1d4ed8", "#7c3aed", "#0d9488"];
  centers.forEach((bx, i) => {
    parts.push(
      `<path d="M ${cx} ${decisionBottom} L ${bx} ${rowTop - 2}" fill="none" stroke="${colors[i]}" stroke-width="1.4" marker-end="url(#sep-uflow-arr)"/>`
    );
  });

  outcomeDefs.forEach((o, i) => {
    const x = startX + i * (boxW + gap);
    parts.push(
      `<rect x="${x}" y="${rowTop}" width="${boxW}" height="${boxH}" rx="8" fill="#fafafa" stroke="${o.color}" stroke-width="2"/>`
    );
    parts.push(
      `<text x="${x + 10}" y="${rowTop + 16}" font-size="10" font-weight="700" fill="${o.color}">${escapeXml(o.title)}</text>`
    );
    o.lines.forEach((ln, j) => {
      parts.push(
        `<text x="${x + 10}" y="${rowTop + padTop + j * lineH}" font-size="8.5" fill="#334155">${escapeXml(ln)}</text>`
      );
    });
  });

  const joinY = rowTop + boxH;
  parts.push(
    `<line x1="${cx}" y1="${joinY}" x2="${cx}" y2="${joinY + 18}" stroke="#64748b" stroke-width="2" marker-end="url(#sep-uflow-arr)"/>`
  );
  const clearY = joinY + 18;
  parts.push(
    `<rect x="80" y="${clearY}" width="760" height="52" rx="8" fill="#fffbeb" stroke="#d97706" stroke-width="1.5"/>`
  );
  parts.push(
    `<text x="${cx}" y="${clearY + 22}" text-anchor="middle" font-size="9.5" font-weight="700" fill="#92400e">Clearance · letters · exit</text>`
  );
  parts.push(
    `<text x="${cx}" y="${clearY + 40}" text-anchor="middle" font-size="8.5" fill="#78350f">Checklist: ${chk.length} task(s) · Task dependencies: ${chk.filter((c) => c.dependsOnTaskId).length} · Templates as configured</text>`
  );
  const H = clearY + 52 + 28;

  return `
      <svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" style="max-width:100%;height:auto">
        <defs>
          <marker id="sep-uflow-arr" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0, 8 4, 0 8" fill="#64748b"/>
          </marker>
        </defs>
        ${parts.join("")}
      </svg>
    `;
}

const SeparationPolicyComponent = ({
  darkMode,
  branchesList = [],
  departmentsList = [],
  designationsList = [],
  subDepartmentsList = [],
  gradesList = [],
  categoriesList = [],
  companyName = MASTERS_DOCUMENT_COMPANY_NAME,
  companyAddress = MASTERS_DOCUMENT_COMPANY_ADDRESS,
  policyPreparedBy = "Human Resources",
  policyApprovedBy = "Management",
  employees = [],
  policies = [],
  onPolicyChange,
}) => {
  const [newPolicy, setNewPolicy] = useState(createDefaultPolicy());
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPolicyId, setEditingPolicyId] = useState(null);
  const [showInactivePolicies, setShowInactivePolicies] = useState(false);
  const [policySearch, setPolicySearch] = useState("");
  const [workflowStepsTab, setWorkflowStepsTab] = useState("resignation");
  const [policyHelpOpen, setPolicyHelpOpen] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [saveGateModal, setSaveGateModal] = useState(null);
  const [policySaveError, setPolicySaveError] = useState(null);
  const [approvalSettingsOpen, setApprovalSettingsOpen] = useState(false);
  const [approvalRolesForPolicy, setApprovalRolesForPolicy] = useState(["management", "ceo"]);
  const [previewPolicy, setPreviewPolicy] = useState(null);
  const [flowchartPolicy, setFlowchartPolicy] = useState(null);
  const [policyDocModal, setPolicyDocModal] = useState(null);
  const [policyHistoryModalId, setPolicyHistoryModalId] = useState(null);
  const [historyPreviewEntry, setHistoryPreviewEntry] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const policyHelpRef = useRef(null);
  const approvalSettingsRef = useRef(null);
  const [allEmployeesList, setAllEmployeesList] = useState([]);
  const [loadingAllEmployees, setLoadingAllEmployees] = useState(false);

  const docCompanyName =
    (typeof companyName === "string" && companyName.trim()) || MASTERS_DOCUMENT_COMPANY_NAME;
  const docCompanyAddress =
    (typeof companyAddress === "string" && companyAddress.trim()) || MASTERS_DOCUMENT_COMPANY_ADDRESS;

  const policyDocumentOptions = useMemo(
    () => ({
      companyName: docCompanyName,
      companyAddress: docCompanyAddress,
      preparedBy: policyPreparedBy,
      approvedBy: policyApprovedBy,
      workflowRoleLabel: (role) => WORKFLOW_ROLES.find((r) => r.value === role)?.label || role || "—",
      branchesList,
      departmentsList,
      designationsList,
      gradesList,
      categoriesList,
      subDepartmentsList,
      locationOptions: LOCATION_OPTIONS,
      employmentTypeOptions: EMPLOYMENT_TYPE_OPTIONS,
    }),
    [
      docCompanyName,
      docCompanyAddress,
      policyPreparedBy,
      policyApprovedBy,
      branchesList,
      departmentsList,
      designationsList,
      gradesList,
      categoriesList,
      subDepartmentsList,
    ]
  );

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingAllEmployees(true);
        const response = await employeeService.getEmployees({ page: 1, limit: 10000 });
        if (response.success && response.employees) setAllEmployeesList(response.employees);
        else if (response.success && response.data) setAllEmployeesList(response.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingAllEmployees(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!policyHelpOpen) return;
    const onMouseDown = (e) => {
      if (policyHelpRef.current && !policyHelpRef.current.contains(e.target)) setPolicyHelpOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [policyHelpOpen]);

  useEffect(() => {
    if (!approvalSettingsOpen) return;
    const onMouseDown = (e) => {
      if (approvalSettingsRef.current && !approvalSettingsRef.current.contains(e.target))
        setApprovalSettingsOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [approvalSettingsOpen]);

  useEffect(() => {
    if (!actionMenu) return;
    const close = () => setActionMenu(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [actionMenu]);

  const checkPolicyLinkedToEmployees = useCallback(
    (policy) => {
      const employeesToCheck = allEmployeesList.length > 0 ? allEmployeesList : employees;
      if (!policy || !employeesToCheck || employeesToCheck.length === 0) {
        return { isLinked: false, linkedEmployees: [], isAllEmployees: false };
      }
      if (policy.status !== "active") {
        return { isLinked: false, linkedEmployees: [], isAllEmployees: false };
      }
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      if (policy.effectiveDate) {
        const effectiveDate = new Date(policy.effectiveDate);
        effectiveDate.setHours(0, 0, 0, 0);
        if (effectiveDate > currentDate) {
          return { isLinked: false, linkedEmployees: [], isAllEmployees: false };
        }
      }
      const assignedTo = policy.assignedTo || {};
      if (assignedTo.allEmployees) {
        return {
          isLinked: true,
          linkedEmployees: employeesToCheck,
          isAllEmployees: true,
          totalEmployeeCount: employeesToCheck.length,
        };
      }
      const linkedEmployees = [];
      employeesToCheck.forEach((employee) => {
        const deptMatch =
          assignedTo.departments?.length > 0 &&
          employee.departmentId &&
          assignedTo.departments.includes(employee.departmentId);
        const empTypeMatch =
          assignedTo.employmentTypes?.length > 0 &&
          employee.employmentType &&
          assignedTo.employmentTypes.includes(employee.employmentType);
        const empStatusMatch =
          assignedTo.employmentStatuses?.length > 0 &&
          employee.employmentStatus &&
          assignedTo.employmentStatuses.includes(employee.employmentStatus);
        const empMatch =
          assignedTo.employees?.length > 0 && employee.id && assignedTo.employees.includes(employee.id);
        if (deptMatch || empTypeMatch || empStatusMatch || empMatch) linkedEmployees.push(employee);
      });
      return {
        isLinked: linkedEmployees.length > 0,
        linkedEmployees,
        isAllEmployees: false,
        totalEmployeeCount: linkedEmployees.length,
      };
    },
    [allEmployeesList, employees]
  );

  const validatePolicyAssignments = useCallback(
    (policy, isEdit = false, editId = null) => {
      const assignedTo = buildAssignedTo(policy);
      const activePolicies = policies.filter(
        (p) =>
          p.status === "active" &&
          (!isEdit || p.id !== editId) &&
          p.effectiveDate &&
          new Date(p.effectiveDate) <= new Date()
      );
      if (assignedTo.allEmployees) {
        const ex = activePolicies.find((p) => p.assignedTo?.allEmployees === true);
        if (ex) {
          return {
            isValid: false,
            error: `A policy for all employees already exists ("${ex.name || ex.policyName}").`,
          };
        }
      }
      if (
        !assignedTo.allEmployees &&
        (assignedTo.departments?.length > 0 ||
          assignedTo.employmentTypes?.length > 0 ||
          assignedTo.employmentStatuses?.length > 0)
      ) {
        const ex = activePolicies.find((p) => p.assignedTo?.allEmployees === true);
        if (ex) {
          return {
            isValid: false,
            error: `An "all employees" policy exists ("${ex.name || ex.policyName}"). Remove it or scope this policy to individuals only.`,
          };
        }
      }
      if (assignedTo.employees?.length > 0) {
        for (const employeeId of assignedTo.employees) {
          const existing = activePolicies.filter((p) => {
            const a = p.assignedTo || {};
            return (
              a.employees?.length > 0 &&
              a.employees.includes(employeeId) &&
              (!isEdit || p.id !== editId)
            );
          });
          if (existing.length > 0) {
            const emp = allEmployeesList.find((e) => e.id === employeeId);
            const name = emp?.user?.name || emp?.empCode || String(employeeId);
            return {
              isValid: false,
              error: `Employee ${name} already has an individual separation policy.`,
            };
          }
        }
      }
      return { isValid: true };
    },
    [policies, allEmployeesList]
  );

  const getPolicySummaryRows = (p) => {
    const indIds = Array.isArray(p.individualEmployeeIds)
      ? p.individualEmployeeIds.join(", ")
      : p.individualEmployeeIds || "";
    const appLabel = (list, value) => {
      if (!value) return "All";
      const found = (list || []).find((x) => String(x.id) === String(value));
      return found?.name || (value ? "—" : "All");
    };
    const rows = [];
    rows.push({ label: "Policy name", value: p.policyName || p.name || "—" });
    rows.push({ label: "Effective date", value: p.effectiveDate || "—" });
    rows.push({ label: "Policy document version", value: String(p.policyVersion ?? 1) });
    rows.push({ label: "Auto-assign on joining", value: p.autoAssignOnJoin ? "Yes" : "No" });
    rows.push({
      label: "Notice — probation (type / days)",
      value: `${NOTICE_PERIOD_TYPES.find((o) => o.value === (p.noticeProbationType || p.noticePeriodType))?.label || "—"} / ${p.defaultNoticeProbationDays ?? p.defaultNoticePeriodDays ?? "—"}d`,
    });
    rows.push({
      label: "Notice — confirmed (type / days)",
      value: `${NOTICE_PERIOD_TYPES.find((o) => o.value === (p.noticeConfirmedType || p.noticePeriodType))?.label || "—"} / ${p.defaultNoticeConfirmedDays ?? p.defaultNoticePeriodDays ?? "—"}d`,
    });
    const noticeCodesLabel = (arr) =>
      Array.isArray(arr) && arr.length
        ? arr.map((v) => NOTICE_PERIOD_STATUS_CODES.find((a) => a.value === v)?.label || v).join(", ")
        : "—";
    const sharedNoticeArr =
      Array.isArray(p.leaveCodesForNotice) && p.leaveCodesForNotice.length
        ? p.leaveCodesForNotice
        : Array.isArray(p.leaveCodesProbation) && p.leaveCodesProbation.length
          ? p.leaveCodesProbation
          : p.attendanceStatusesForNotice?.length
            ? p.attendanceStatusesForNotice
            : p.attendanceStatusesProbation;
    rows.push({
      label: "Notice period status codes (working-day notice)",
      value: noticeCodesLabel(sharedNoticeArr),
    });
    rows.push({ label: "Exit interview mandatory", value: p.exitInterviewMandatory === "yes" ? "Yes" : "No" });
    if (p.exitInterviewMandatory === "yes") {
      rows.push({
        label: "Exit feedback template",
        value:
          FEEDBACK_TEMPLATE_OPTIONS.find((o) => o.value === p.exitInterviewFeedbackTemplate)?.label ||
          p.exitInterviewFeedbackTemplate ||
          "—",
      });
    }
    rows.push({ label: "Allow backdated resignation", value: p.allowBackdatedResignation ? "Yes" : "No" });
    rows.push({
      label: "Backdated proof mandatory",
      value: p.allowBackdatedResignation ? (p.backdatedProofMandatory ? "Yes" : "No") : "—",
    });
    rows.push({
      label: "Approval method",
      value:
        p.approvalMethod === "workflow"
          ? "Through workflow"
          : p.approvalMethod === "on_instance"
            ? "On instance"
            : "Auto approved",
    });
    rows.push({ label: "Reminder days", value: p.reminderDays ?? "—" });
    rows.push({ label: "Auto letter (resignation path)", value: p.autoLetter === "yes" ? "Yes" : "No" });
    rows.push({ label: "Auto letter (termination path)", value: p.autoLetterTermination === "yes" ? "Yes" : "No" });
    rows.push({
      label: "Resignation acceptance letter template",
      value:
        LETTER_RESIGNATION_OPTIONS.find((o) => o.value === p.letterResignationAcceptance)?.label ||
        p.letterResignationAcceptance ||
        "—",
    });
    const stepSummary = (s) => {
      const role = WORKFLOW_ROLES.find((r) => r.value === s.role)?.label || s.role;
      const w = s.noticeWaiverAllowed
        ? ` | Notice waiver: up to ${s.noticeWaiverMaxDays ?? 0}d`
        : "";
      return `${role} — SLA ${s.slaDays ?? 3}d${s.notifyOnly ? " (notify)" : ""}${w}`;
    };
    (Array.isArray(p.workflowSteps) ? p.workflowSteps : []).forEach((s, i) =>
      rows.push({ label: `Resignation workflow step ${i + 1}`, value: stepSummary(s) })
    );
    (Array.isArray(p.terminationWorkflowSteps) ? p.terminationWorkflowSteps : []).forEach((s, i) =>
      rows.push({ label: `Termination workflow step ${i + 1}`, value: stepSummary(s) })
    );
    (Array.isArray(p.cancellationWorkflowSteps) ? p.cancellationWorkflowSteps : []).forEach((s, i) =>
      rows.push({ label: `Cancellation workflow step ${i + 1}`, value: stepSummary(s) })
    );
    (Array.isArray(p.checklistMappings) ? p.checklistMappings : []).forEach((item, i) => {
      const dep =
        item.dependsOnTaskId &&
        (p.checklistMappings || []).find((x) => x.taskId === item.dependsOnTaskId);
      rows.push({
        label: `Checklist ${i + 1}`,
        value: `${item.name || "—"} | After: ${dep?.name || item.dependsOnTaskId || "—"}`,
      });
    });
    rows.push({ label: "All employees", value: p.allEmployees ? "Yes" : "No" });
    rows.push({
      label: "Employment types",
      value:
        Array.isArray(p.employmentTypes) && p.employmentTypes.length
          ? p.employmentTypes.map((v) => EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v).join(", ")
          : "All",
    });
    rows.push({ label: "Branch", value: appLabel(branchesList, p.branch) });
    rows.push({ label: "Department", value: appLabel(departmentsList, p.department) });
    rows.push({ label: "Designation", value: appLabel(designationsList, p.designation) });
    rows.push({ label: "Grade", value: appLabel(gradesList, p.grade) });
    rows.push({ label: "Category", value: appLabel(categoriesList, p.category) });
    rows.push({ label: "Sub-department", value: appLabel(subDepartmentsList, p.subDepartment) });
    rows.push({
      label: "Location",
      value: LOCATION_OPTIONS.find((o) => o.value === (p.location || ""))?.label || "All",
    });
    rows.push({ label: "Individual employee IDs", value: indIds || "—" });
    return rows;
  };

  const getDynamicPolicyGuideLines = (policy) => {
    const p = policy || createDefaultPolicy();
    return [
      `Probation notice: ${p.defaultNoticeProbationDays ?? 30}d (${p.noticeProbationType || "calendar"}). Confirmed: ${p.defaultNoticeConfirmedDays ?? 30}d (${p.noticeConfirmedType || "calendar"}).`,
      `Per workflow step you can allow notice waiver up to a max day limit for that approver role.`,
      `Checklist tasks can depend on another task before they can be completed.`,
      `Applicability uses the same master fields as confirmation policies.`,
    ];
  };

  const selectedApprovalRoleLabels = useMemo(
    () =>
      APPROVAL_ROLES.filter((r) => approvalRolesForPolicy.includes(r.value)).map((r) => r.label),
    [approvalRolesForPolicy]
  );

  const openCreateForm = () => {
    setIsEditing(false);
    setEditingPolicyId(null);
    setWorkflowStepsTab("resignation");
    setPolicySaveError(null);
    setSaveGateModal(null);
    setNewPolicy(createDefaultPolicy());
    setIsFormOpen(true);
  };

  const openEditForm = (policy) => {
    setIsEditing(true);
    setEditingPolicyId(policy.id);
    setWorkflowStepsTab("resignation");
    setPolicySaveError(null);
    setSaveGateModal(null);
    setNewPolicy(normalizePolicyRow(policy));
    setIsFormOpen(true);
  };

  const toggleApprovalRole = (roleValue) => {
    setApprovalRolesForPolicy((prev) =>
      prev.includes(roleValue) ? prev.filter((r) => r !== roleValue) : [...prev, roleValue]
    );
  };

  const toggleEmploymentType = (value) => {
    setNewPolicy((prev) => {
      const cur = Array.isArray(prev.employmentTypes) ? prev.employmentTypes : [];
      const has = cur.includes(value);
      return { ...prev, employmentTypes: has ? cur.filter((v) => v !== value) : [...cur, value] };
    });
  };

  const addWorkflowStep = (key) => {
    setNewPolicy((prev) => ({
      ...prev,
      [key]: [
        ...(prev[key] || []),
        normalizeWorkflowStep({
          role: "manager",
          slaDays: 3,
          notifyOnly: false,
          noticeWaiverAllowed: false,
          noticeWaiverMaxDays: 0,
        }),
      ],
    }));
  };
  const removeWorkflowStep = (key, index) => {
    setNewPolicy((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((_, i) => i !== index),
    }));
  };
  const updateWorkflowStep = (key, index, field, value) => {
    setNewPolicy((prev) => ({
      ...prev,
      [key]: (prev[key] || []).map((step, i) => (i === index ? { ...step, [field]: value } : step)),
    }));
  };

  const addChecklistItem = () => {
    setNewPolicy((prev) => ({
      ...prev,
      checklistMappings: [...(prev.checklistMappings || []), normalizeChecklistItem({})],
    }));
  };
  const removeChecklistItem = (index) => {
    setNewPolicy((prev) => {
      const list = prev.checklistMappings || [];
      const removed = list[index];
      const rid = removed?.taskId;
      const next = list
        .filter((_, i) => i !== index)
        .map((item) =>
          rid && item.dependsOnTaskId === rid ? { ...item, dependsOnTaskId: "" } : item
        );
      return { ...prev, checklistMappings: next };
    });
  };
  const updateChecklistItem = (index, field, value) => {
    setNewPolicy((prev) => ({
      ...prev,
      checklistMappings: (prev.checklistMappings || []).map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  /** Single shared list — persisted on all leaveCodes* / attendanceStatuses* / leaveCodesForNotice fields for API compatibility. */
  const toggleNoticePeriodCode = (value) => {
    setNewPolicy((prev) => {
      const cur = Array.isArray(prev.leaveCodesForNotice) ? prev.leaveCodesForNotice : [];
      const has = cur.includes(value);
      const next = has ? cur.filter((x) => x !== value) : [...cur, value];
      return {
        ...prev,
        leaveCodesForNotice: next,
        attendanceStatusesForNotice: next,
        leaveCodesProbation: next,
        leaveCodesConfirmed: next,
        attendanceStatusesProbation: next,
        attendanceStatusesConfirmed: next,
      };
    });
  };

  const requestSavePolicyForm = (opts = {}) => {
    const skipOverlapCheck = !!opts.skipOverlapCheck;
    setPolicySaveError(null);
    setSaveGateModal(null);
    const normalizedIndividualIds = String(newPolicy.individualEmployeeIds || "")
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ");
    const candidate = {
      ...newPolicy,
      individualEmployeeIds: normalizedIndividualIds,
      employmentTypes: Array.isArray(newPolicy.employmentTypes) ? newPolicy.employmentTypes : [],
    };

    if (isEditing && editingPolicyId) {
      const saved = policies.find((p) => p.id === editingPolicyId);
      if (saved) {
        const sigOld = policyContentSignature(normalizePolicyRow(saved));
        const sigNew = policyContentSignature({ ...candidate, id: saved.id });
        if (sigOld !== sigNew && (saved.effectiveDate || "") === (candidate.effectiveDate || "")) {
          setSaveGateModal({ type: "effective_date" });
          return;
        }
      }
    }

    if (!skipOverlapCheck) {
      const conflicts = findConflictingPolicies(
        { ...candidate, id: candidate.id || editingPolicyId || "__new__" },
        policies.map((p) => normalizePolicyRow(p)),
        isEditing ? editingPolicyId : null
      );
      if (conflicts.length > 0) {
        setSaveGateModal({
          type: "overlap",
          items: conflicts.map((p) => ({
            id: p.id,
            name: p.policyName || p.name || "Untitled",
            detail: summarizeOverlapReason(candidate, p),
          })),
        });
        return;
      }
    }

    if (!String(newPolicy.policyName || "").trim()) {
      setPolicySaveError("Policy name is required.");
      return;
    }
    if (newPolicy.approvalMethod === "workflow" && (!newPolicy.workflowSteps || newPolicy.workflowSteps.length === 0)) {
      setPolicySaveError("Add at least one resignation workflow step when using Through workflow.");
      return;
    }
    const v = validatePolicyAssignments(candidate, isEditing, editingPolicyId);
    if (!v.isValid) {
      setPolicySaveError(v.error);
      return;
    }
    setShowSummaryModal(true);
  };

  const continueAfterOverlapAck = () => {
    setSaveGateModal(null);
    requestSavePolicyForm({ skipOverlapCheck: true });
  };

  const handleSavePolicy = async () => {
    const normalizedIndividualIds = String(newPolicy.individualEmployeeIds || "")
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ");
    const form = {
      ...newPolicy,
      individualEmployeeIds: normalizedIndividualIds,
      employmentTypes: Array.isArray(newPolicy.employmentTypes) ? newPolicy.employmentTypes : [],
    };

    if (isEditing && editingPolicyId) {
      const saved = policies.find((p) => p.id === editingPolicyId);
      if (saved) {
        const sigOld = policyContentSignature(normalizePolicyRow(saved));
        const sigNew = policyContentSignature({ ...form, id: saved.id });
        if (sigOld !== sigNew && (saved.effectiveDate || "") === (form.effectiveDate || "")) {
          toast.error("Update the effective date when policy content changes.");
          return;
        }
      }
    }

    const basePayload = mergePayloadForApi(form);
    const savedAt = new Date().toISOString();
    let merged;
    if (isEditing && editingPolicyId) {
      const prev = policies.find((p) => p.id === editingPolicyId);
      if (!prev) {
        toast.error("Policy not found");
        return;
      }
      const v = computeVersionOnSave(normalizePolicyRow(prev), basePayload, false);
      merged = {
        ...normalizePolicyRow(prev),
        ...basePayload,
        policyVersion: v.policyVersion,
        policyVersionHistory: v.policyVersionHistory,
        approvalRolesForPolicy,
        updatedAt: savedAt,
        policyDocumentPushed: prev.policyDocumentPushed,
        policyPushedAt: prev.policyPushedAt,
      };
      merged.policySnapshots = mergePolicySnapshots(normalizePolicyRow(prev), merged, v, false, savedAt);
      try {
        const res = await separationPolicyService.updateSeparationPolicy(editingPolicyId, merged);
        if (res.success) {
          toast.success(
            v.versionBumped
              ? "Policy saved — document version increased for new future effective date."
              : "Separation policy updated"
          );
          onPolicyChange?.();
          setIsFormOpen(false);
          setIsEditing(false);
          setEditingPolicyId(null);
          setShowSummaryModal(false);
        } else toast.error(res.message || "Update failed");
      } catch (e) {
        toast.error(e.response?.data?.message || e.message || "Update failed");
      }
    } else {
      const v = computeVersionOnSave(null, basePayload, true);
      merged = {
        ...basePayload,
        policyVersion: v.policyVersion,
        policyVersionHistory: v.policyVersionHistory,
        status: "active",
        approvalRolesForPolicy,
        createdAt: savedAt,
        updatedAt: savedAt,
        policyDocumentPushed: false,
        policyPushedAt: null,
      };
      merged.policySnapshots = mergePolicySnapshots(null, merged, v, true, savedAt);
      try {
        const res = await separationPolicyService.createSeparationPolicy(merged);
        if (res.success) {
          toast.success("Separation policy created");
          onPolicyChange?.();
          setIsFormOpen(false);
          setShowSummaryModal(false);
        } else toast.error(res.message || "Create failed");
      } catch (e) {
        toast.error(e.response?.data?.message || e.message || "Create failed");
      }
    }
  };

  const handleCancelPolicy = () => {
    setIsFormOpen(false);
    setIsEditing(false);
    setEditingPolicyId(null);
    setWorkflowStepsTab("resignation");
    setPolicySaveError(null);
    setSaveGateModal(null);
  };

  const handlePushPolicyDocument = (policy) => {
    const now = new Date().toISOString();
    onPolicyChange?.();
    toast.success("Policy document publish requested — refresh list to confirm.");
    setPolicyDocModal((m) =>
      m && m.id === policy.id ? { ...m, policyDocumentPushed: true, policyPushedAt: now } : m
    );
    setActionMenu(null);
  };

  const openPolicyDocumentPreview = (policy) => {
    setPolicyDocModal(policies.find((p) => p.id === policy.id) || policy);
    setActionMenu(null);
  };

  const printPolicyFullDocument = (policy) => {
    const html = buildSeparationPolicyDocumentHtml(normalizePolicyRow(policy), policyDocumentOptions);
    printSeparationPolicyDocumentHtml(html);
  };

  const printPolicySnapshotDocument = (snapshotPolicy) => {
    const html = buildSeparationPolicyDocumentHtml(snapshotPolicy, policyDocumentOptions);
    printSeparationPolicyDocumentHtml(html);
  };

  const openActionMenuFromEvent = (e, policy) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 220;
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
    const top = rect.bottom + 4;
    setActionMenu((prev) => (prev?.policy?.id === policy.id ? null : { policy, top, left }));
  };

  const printAllPolicies = () => {
    printAllPoliciesAsDocument("Separation Policy", policies, (p) => getPolicySummaryRows(normalizePolicyRow(p)));
  };

  const printPolicyDocument = (policy) => {
    printSinglePolicyAsDocument(
      "Separation Policy",
      policy.policyName || policy.name,
      policy.effectiveDate,
      (p) => getPolicySummaryRows(normalizePolicyRow(p)),
      policy
    );
  };

  const printCombinedFlowchartPdf = (policy) => {
    const html = `
      <html>
      <head>
        <title>Separation process — ${policy?.policyName || "Policy"}</title>
        <style>
          body { font-family: "Segoe UI", Inter, Arial, sans-serif; margin: 20px; color: #0f172a; }
          h2 { margin: 0 0 12px 0; font-size: 16px; }
          .hint { font-size: 12px; color: #475569; margin-bottom: 14px; }
          .flow { border: 1px solid #e2e8f0; padding: 12px; border-radius: 8px; page-break-inside: avoid; }
          @media print { .flow { break-inside: avoid; } body { margin: 12px; } }
        </style>
      </head>
      <body>
        <h2>Separation process — ${policy?.policyName || "Policy"}</h2>
        <div class="hint">Single end-to-end flow (print or Save as PDF from the dialog).</div>
        <div class="flow">${buildSeparationFlowchartSvg(policy || {})}</div>
      </body>
      </html>
    `;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const getApplicabilityBadges = (policy) => {
    const p = normalizePolicyRow(policy);
    const badges = [];
    if (p.allEmployees) badges.push("All employees");
    if (!p.allEmployees) {
      const addBadge = (list, key, prefix) => {
        if (!p[key]) return;
        const item = (list || []).find((x) => String(x.id) === String(p[key]));
        badges.push(`${prefix}: ${item?.name || p[key]}`);
      };
      addBadge(branchesList, "branch", "Branch");
      addBadge(departmentsList, "department", "Dept");
      addBadge(designationsList, "designation", "Desig");
      addBadge(gradesList, "grade", "Grade");
      addBadge(categoriesList, "category", "Cat");
      addBadge(subDepartmentsList, "subDepartment", "Sub");
    }
    if (Array.isArray(p.employmentTypes) && p.employmentTypes.length) {
      badges.push(
        `Employment: ${p.employmentTypes.map((v) => EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v).join(", ")}`
      );
    }
    return badges.length ? badges : ["No scope"];
  };

  const getApprovalLevelsCount = (policy) => {
    const p = normalizePolicyRow(policy);
    if (p.approvalMethod === "workflow") {
      const a = (p.workflowSteps || []).length;
      const b = (p.terminationWorkflowSteps || []).length;
      return `${a}+${b}`;
    }
    return "—";
  };

  const handleDeactivatePolicy = async (policyId) => {
    const policy = policies.find((p) => p.id === policyId);
    if (!policy) return;
    const { isLinked, linkedEmployees, isAllEmployees, totalEmployeeCount } =
      checkPolicyLinkedToEmployees(policy);
    if (isLinked) {
      let msg;
      if (isAllEmployees) {
        msg = `This policy applies to ALL employees (${totalEmployeeCount}). Deactivate anyway?`;
      } else {
        msg = `Linked to ${linkedEmployees.length} employee(s). Deactivate anyway?`;
      }
      if (!window.confirm(msg)) return;
    }
    try {
      const res = await separationPolicyService.deactivateSeparationPolicy(policyId);
      if (res.success) {
        toast.success("Policy deactivated");
        onPolicyChange?.();
      } else toast.error(res.message || "Failed");
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed");
    }
  };

  const handleReactivatePolicy = async (policyId) => {
    const policy = policies.find((p) => p.id === policyId);
    if (!policy) return;
    try {
      const res = await separationPolicyService.updateSeparationPolicy(policyId, {
        ...policy,
        status: "active",
      });
      if (res.success) {
        toast.success("Policy reactivated");
        onPolicyChange?.();
      } else toast.error(res.message || "Failed");
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed");
    }
  };

  const handleDeletePolicy = async (policyId) => {
    const policy = policies.find((p) => p.id === policyId);
    if (!policy) return;
    const { isLinked, linkedEmployees, isAllEmployees } = checkPolicyLinkedToEmployees(policy);
    if (isLinked) {
      toast.error(
        isAllEmployees
          ? "Cannot delete: policy applies to all employees. Deactivate instead."
          : `Cannot delete: linked to ${linkedEmployees.length} employee(s).`
      );
      return;
    }
    if (!window.confirm("Delete this separation policy?")) return;
    try {
      const res = await separationPolicyService.deleteSeparationPolicy(policyId);
      if (res.success) {
        toast.success("Deleted");
        onPolicyChange?.();
      } else toast.error(res.message || "Failed");
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed");
    }
  };

  const handleCopyPolicy = async (policy) => {
    const p = normalizePolicyRow(policy);
    const nm = `${p.policyName || "Policy"} (Copy)`;
    const eff = p.effectiveDate || new Date().toISOString().slice(0, 10);
    const copied = {
      ...createDefaultPolicy(),
      ...p,
      policyName: nm,
      name: nm,
      effectiveDate: eff,
      status: "active",
      policyVersion: 1,
      policyVersionHistory: [
        { version: 1, effectiveDate: eff, savedAt: new Date().toISOString(), event: "created", policyName: nm },
      ],
      policyDocumentPushed: false,
      policyPushedAt: null,
    };
    const snap = clonePolicySnapshot({ ...copied, policySnapshots: undefined, policyVersion: 1 });
    copied.policySnapshots = [{ version: 1, effectiveDate: eff, savedAt: new Date().toISOString(), policy: snap }];
    try {
      const res = await separationPolicyService.createSeparationPolicy(copied);
      if (res.success) {
        toast.success("Policy copied");
        onPolicyChange?.();
      } else toast.error(res.message || "Copy failed");
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Copy failed");
    }
  };

  const visiblePolicies = policies
    .filter((p) => (showInactivePolicies ? p.status === "inactive" : p.status === "active"))
    .filter((p) =>
      String(p.policyName || p.name || "")
        .toLowerCase()
        .includes(String(policySearch || "").toLowerCase().trim())
    );
  const listedPolicies = visiblePolicies.slice(0, 10);

  const wfKey =
    workflowStepsTab === "termination"
      ? "terminationWorkflowSteps"
      : workflowStepsTab === "cancellation"
        ? "cancellationWorkflowSteps"
        : "workflowSteps";

  const inputCls = `w-full py-1.5 px-2.5 border rounded-lg text-sm ${
    darkMode ? "bg-slate-800 border-slate-600 text-white" : "bg-white border-slate-200"
  }`;
  const labelCls = `block text-xs font-medium mb-0.5 ${darkMode ? "text-slate-300" : "text-slate-600"}`;
  const sectionCls = `mt-3 p-3 border rounded-xl shadow-sm ${
    darkMode ? "border-slate-600 bg-slate-800/60" : "border-slate-200/90 bg-white"
  }`;

  return (
    <div
      className={`p-4 rounded-none border ${
        darkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-200"
      } h-full`}
    >
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className={`text-base font-semibold ${darkMode ? "text-white" : "text-gray-800"}`}>
          Separation Policies (Resignation &amp; Termination)
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative" ref={approvalSettingsRef}>
            <button
              type="button"
              onClick={() => setApprovalSettingsOpen((v) => !v)}
              className="px-3 py-1.5 text-sm rounded-none border bg-white text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
            >
              <Settings size={14} /> Roles
            </button>
            {approvalSettingsOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 shadow-lg z-20 p-2">
                <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase border-b mb-2">
                  Roles that can approve policy
                </div>
                {APPROVAL_ROLES.map((role) => (
                  <label key={role.value} className="flex items-center gap-2 px-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      checked={approvalRolesForPolicy.includes(role.value)}
                      onChange={() => toggleApprovalRole(role.value)}
                    />
                    {role.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={printAllPolicies}
            className="px-3 py-1.5 text-sm rounded-none border bg-white text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
          >
            <Printer size={14} /> Print all policies
          </button>
          <input
            type="text"
            value={policySearch}
            onChange={(e) => setPolicySearch(e.target.value)}
            placeholder="Search policy by name..."
            className={`w-64 p-2 border rounded-none text-sm ${
              darkMode ? "bg-gray-800 border-gray-500 text-white" : "bg-white border-gray-300"
            }`}
          />
          <button
            type="button"
            onClick={() => setShowInactivePolicies((v) => !v)}
            className={`px-3 py-1.5 text-sm rounded-none ${
              darkMode ? "bg-gray-600 text-white hover:bg-gray-500" : "bg-gray-100 text-gray-700 hover:bg-gray-200 border"
            }`}
          >
            {showInactivePolicies ? "Show Active" : "Show Inactive"}
          </button>
          <button
            type="button"
            onClick={openCreateForm}
            className="px-3 py-1.5 text-sm rh-btn-primary hover:opacity-95 rounded-none"
          >
            + New Separation Policy
          </button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <div className="max-h-[62vh] overflow-y-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead className="bg-slate-100 border-b border-slate-200 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 font-semibold text-slate-700 w-12">#</th>
                <th className="px-4 py-3 font-semibold text-slate-700">Policy Name</th>
                <th className="px-4 py-3 font-semibold text-slate-700 text-center w-16">Ver.</th>
                <th className="px-4 py-3 font-semibold text-slate-700">Notice</th>
                <th className="px-4 py-3 font-semibold text-slate-700">Reminder</th>
                <th className="px-4 py-3 font-semibold text-slate-700">Approval</th>
                <th className="px-4 py-3 font-semibold text-slate-700 text-center">WF (R+T)</th>
                <th className="px-4 py-3 font-semibold text-slate-700 min-w-[120px]">Scope</th>
                <th className="px-4 py-3 font-semibold text-slate-700">Status</th>
                <th className="px-4 py-3 font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {listedPolicies.map((policy, idx) => {
                const p = normalizePolicyRow(policy);
                const badges = getApplicabilityBadges(policy);
                return (
                  <tr key={policy.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 text-slate-600">{idx + 1}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{p.policyName || p.name}</td>
                    <td className="px-4 py-3 text-center tabular-nums text-slate-700">v{p.policyVersion ?? 1}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {p.defaultNoticePeriodDays ?? p.noticePeriodDays ?? "—"}d ({p.noticePeriodType || "calendar"})
                    </td>
                    <td className="px-4 py-3 text-slate-700">{p.reminderDays ?? "—"}d</td>
                    <td className="px-4 py-3 text-slate-700">
                      {p.approvalMethod === "workflow"
                        ? "Workflow"
                        : p.approvalMethod === "on_instance"
                          ? "On instance"
                          : "Auto approved"}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-700">{getApprovalLevelsCount(policy)}</td>
                    <td className="px-4 py-3">
                      <span className="flex flex-wrap gap-1">
                        {badges.slice(0, 3).map((b, i) => (
                          <span
                            key={i}
                            className="inline-flex px-2 py-0.5 text-[10px] rounded-none bg-slate-100 text-slate-700 border border-slate-200"
                          >
                            {b}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-md ${
                          policy.status === "active"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-slate-100 text-slate-500 border border-slate-200"
                        }`}
                      >
                        {policy.status || "active"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        aria-label="Row actions"
                        className="inline-flex items-center justify-center p-1.5 rounded-none border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        onClick={(e) => openActionMenuFromEvent(e, policy)}
                      >
                        <MoreVertical size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {listedPolicies.length === 0 && (
        <p className={`text-sm py-6 text-center ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
          {policySearch.trim() ? "No policies match your search." : "No policies. Create a separation policy."}
        </p>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto py-6 px-3 bg-black/45 backdrop-blur-[2px]">
          <div
            className={`my-2 w-[min(96vw,1100px)] max-h-[min(90vh,900px)] rounded-none shadow-lg border flex flex-col overflow-hidden ${
              darkMode ? "bg-slate-900 border-slate-600 text-slate-100" : "bg-white border-gray-300 text-slate-800"
            }`}
          >
            <div
              className={`px-4 py-3 flex items-center justify-between flex-shrink-0 border-b ${
                darkMode ? "border-slate-600" : "border-gray-200"
              }`}
            >
              <div>
                <h3 className="text-[15px] font-semibold">
                  {isEditing ? "Edit separation policy" : "New separation policy"}
                </h3>
                <p className={`text-[11px] mt-0.5 ${darkMode ? "text-slate-400" : "text-gray-500"}`}>
                  Resignation and termination rules, workflows, letters, checklist, applicability.
                </p>
              </div>
              <div className="flex items-center gap-2 relative" ref={policyHelpRef}>
                <button
                  type="button"
                  onClick={() => setPolicyHelpOpen((v) => !v)}
                  className={darkMode ? "text-slate-400 hover:text-slate-200" : "text-gray-500 hover:text-gray-800"}
                >
                  <Info size={18} />
                </button>
                <button
                  type="button"
                  onClick={handleCancelPolicy}
                  className={darkMode ? "text-slate-400 hover:text-slate-200" : "text-gray-500 hover:text-gray-800"}
                >
                  <X size={18} />
                </button>
                {policyHelpOpen && (
                  <div className="absolute right-0 top-8 w-[400px] max-w-[85vw] p-3 bg-white border border-gray-200 shadow-lg z-20 text-xs text-gray-700 space-y-1">
                    {getDynamicPolicyGuideLines(newPolicy).map((line, idx) => (
                      <p key={idx}>{line}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div
              className={`flex-1 overflow-y-auto px-4 py-2.5 text-[12px] leading-snug ${
                darkMode ? "bg-slate-900/50" : "bg-slate-50/80"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Policy name</label>
                  <input
                    className={inputCls}
                    value={newPolicy.policyName || ""}
                    onChange={(e) => setNewPolicy((p) => ({ ...p, policyName: e.target.value }))}
                    placeholder="e.g. Default Separation Policy"
                  />
                </div>
                <div>
                  <label className={labelCls}>Effective date</label>
                  <input
                    type="date"
                    className={inputCls}
                    value={newPolicy.effectiveDate || ""}
                    onChange={(e) => setNewPolicy((p) => ({ ...p, effectiveDate: e.target.value }))}
                  />
                  <p className="text-[10px] mt-1 text-slate-500">
                    Version bumps when effective date moves forward to a future date after the previous (see confirmation
                    policy rules).
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      checked={!!newPolicy.autoAssignOnJoin}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, autoAssignOnJoin: e.target.checked }))}
                      className="mr-2"
                    />
                    Auto-assign this policy on joining
                  </label>
                </div>
              </div>

              <div className={sectionCls}>
                <h4 className="text-xs font-semibold uppercase tracking-wide mb-2 text-slate-700">
                  Resignation — notice &amp; dates (probation vs confirmed)
                </h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className={`p-3 border rounded-lg ${darkMode ? "border-slate-600 bg-slate-800/40" : "border-slate-200 bg-white"}`}>
                    <div className="text-xs font-semibold text-slate-700 mb-2">Probation</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className={labelCls}>Notice type</label>
                        <select
                          className={inputCls}
                          value={newPolicy.noticeProbationType || "calendar"}
                          onChange={(e) => setNewPolicy((p) => ({ ...p, noticeProbationType: e.target.value }))}
                        >
                          {NOTICE_PERIOD_TYPES.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Default days</label>
                        <input
                          type="number"
                          min={1}
                          className={inputCls}
                          value={newPolicy.defaultNoticeProbationDays ?? 30}
                          onChange={(e) =>
                            setNewPolicy((p) => ({
                              ...p,
                              defaultNoticeProbationDays: Number(e.target.value) || 30,
                            }))
                          }
                        />
                      </div>
                    </div>
                    {newPolicy.noticeProbationType === "working" && (
                      <p
                        className={`text-[10px] font-medium mt-3 ${darkMode ? "text-emerald-400" : "text-emerald-800"}`}
                      >
                        Used for probation — working-day notice is active.
                      </p>
                    )}
                  </div>
                  <div className={`p-3 border rounded-lg ${darkMode ? "border-slate-600 bg-slate-800/40" : "border-slate-200 bg-white"}`}>
                    <div className="text-xs font-semibold text-slate-700 mb-2">Confirmed</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className={labelCls}>Notice type</label>
                        <select
                          className={inputCls}
                          value={newPolicy.noticeConfirmedType || "calendar"}
                          onChange={(e) => setNewPolicy((p) => ({ ...p, noticeConfirmedType: e.target.value }))}
                        >
                          {NOTICE_PERIOD_TYPES.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Default days</label>
                        <input
                          type="number"
                          min={1}
                          className={inputCls}
                          value={newPolicy.defaultNoticeConfirmedDays ?? 30}
                          onChange={(e) =>
                            setNewPolicy((p) => ({
                              ...p,
                              defaultNoticeConfirmedDays: Number(e.target.value) || 30,
                              defaultNoticePeriodDays: Number(e.target.value) || 30,
                            }))
                          }
                        />
                      </div>
                    </div>
                    {newPolicy.noticeConfirmedType === "working" && (
                      <p
                        className={`text-[10px] font-medium mt-3 ${darkMode ? "text-emerald-400" : "text-emerald-800"}`}
                      >
                        Used for confirmed employees — working-day notice is active.
                      </p>
                    )}
                  </div>
                </div>
                <div
                  className={`mt-4 p-3 border rounded-lg ${darkMode ? "border-slate-600 bg-slate-800/40" : "border-slate-200 bg-white"}`}
                >
                  <div className="text-xs font-semibold text-slate-700 mb-2">
                    Attendance / status codes for notice period (probation &amp; confirmed)
                  </div>
                  <p
                    className={`text-[10px] font-medium mb-2 ${darkMode ? "text-amber-400/90" : "text-amber-900"}`}
                  >
                    Used when probation or confirmed notice type is set to Working days (by attendance) above.
                  </p>
                  <label className={`${labelCls}`}>
                    Count as one working day toward notice (same list applies to both groups)
                  </label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {NOTICE_PERIOD_STATUS_CODES.map((o) => (
                      <label key={`np-${o.value}`} className="flex items-center text-[11px]">
                        <input
                          type="checkbox"
                          className="mr-1"
                          checked={(newPolicy.leaveCodesForNotice || []).includes(o.value)}
                          onChange={() => toggleNoticePeriodCode(o.value)}
                        />
                        {o.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className={`mt-4 p-4 border rounded-lg ${darkMode ? "border-slate-600 bg-slate-800/40" : "border-slate-200 bg-white"}`}>
                  <div className="font-semibold text-slate-800 mb-2 text-sm">Backdated resignation</div>
                  <label className="flex items-center gap-2 cursor-pointer mb-3">
                    <input
                      type="checkbox"
                      checked={!!newPolicy.allowBackdatedResignation}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, allowBackdatedResignation: e.target.checked }))
                      }
                      className="rounded border-slate-300 text-redhalo-navy focus:ring-redhalo-red/35/20"
                    />
                    <span className="text-slate-700 text-sm font-medium">Allow backdated resignation</span>
                  </label>
                  {newPolicy.allowBackdatedResignation && (
                    <div className="pl-6 border-l-2 border-slate-200">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!newPolicy.backdatedProofMandatory}
                          onChange={(e) =>
                            setNewPolicy((p) => ({ ...p, backdatedProofMandatory: e.target.checked }))
                          }
                          className="rounded border-slate-300 text-redhalo-navy focus:ring-redhalo-red/35/20"
                        />
                        <span className="text-slate-700 text-sm">
                          Proof of document upload mandatory (employee must upload proof when resignation date is in the
                          past)
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              <div className={sectionCls}>
                <h4 className="text-xs font-semibold uppercase tracking-wide mb-2 text-slate-700">
                  Letters &amp; templates
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <label className={labelCls}>Auto resignation letter</label>
                    <select
                      className={inputCls}
                      value={newPolicy.autoLetter}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, autoLetter: e.target.value }))}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Auto termination letter</label>
                    <select
                      className={inputCls}
                      value={newPolicy.autoLetterTermination || "no"}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, autoLetterTermination: e.target.value }))}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Resignation / relieving template</label>
                    <select
                      className={inputCls}
                      value={newPolicy.letterResignation || ""}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, letterResignation: e.target.value }))}
                    >
                      {LETTER_RESIGNATION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Resignation acceptance letter</label>
                    <select
                      className={inputCls}
                      value={newPolicy.letterResignationAcceptance || ""}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, letterResignationAcceptance: e.target.value }))
                      }
                    >
                      {LETTER_RESIGNATION_OPTIONS.map((o) => (
                        <option key={`acc-${o.value}`} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Termination letter template</label>
                    <select
                      className={inputCls}
                      value={newPolicy.letterTermination || ""}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, letterTermination: e.target.value }))}
                    >
                      {LETTER_TERMINATION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Cancellation letter (resignation)</label>
                    <select
                      className={inputCls}
                      value={newPolicy.letterCancellation || ""}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, letterCancellation: e.target.value }))}
                    >
                      {LETTER_RESIGNATION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={!!newPolicy.useCancellationLetter}
                        onChange={(e) =>
                          setNewPolicy((p) => ({ ...p, useCancellationLetter: e.target.checked }))
                        }
                      />
                      Use cancellation letter on assignment
                    </label>
                    {newPolicy.useCancellationLetter && (
                      <select
                        className={inputCls}
                        value={newPolicy.cancellationLetterFormat || ""}
                        onChange={(e) =>
                          setNewPolicy((p) => ({ ...p, cancellationLetterFormat: e.target.value }))
                        }
                      >
                        {LETTER_RESIGNATION_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </div>

              <div className={sectionCls}>
                <h4 className="text-xs font-semibold uppercase tracking-wide mb-2 text-slate-700">Exit interview</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Exit interview mandatory</label>
                    <select
                      className={inputCls}
                      value={newPolicy.exitInterviewMandatory || "no"}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, exitInterviewMandatory: e.target.value }))}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  {newPolicy.exitInterviewMandatory === "yes" && (
                    <div>
                      <label className={labelCls}>Feedback template</label>
                      <select
                        className={inputCls}
                        value={newPolicy.exitInterviewFeedbackTemplate || ""}
                        onChange={(e) =>
                          setNewPolicy((p) => ({ ...p, exitInterviewFeedbackTemplate: e.target.value }))
                        }
                      >
                        {FEEDBACK_TEMPLATE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              <div className={sectionCls}>
                <h4 className="text-xs font-semibold uppercase tracking-wide mb-2 text-slate-700">Workflow</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                  <div>
                    <label className={labelCls}>Approval method</label>
                    <select
                      className={inputCls}
                      value={newPolicy.approvalMethod}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, approvalMethod: e.target.value }))}
                    >
                      <option value="auto_approved">Auto approved</option>
                      <option value="workflow">Through workflow</option>
                      <option value="on_instance">On instance</option>
                    </select>
                  </div>
                  {newPolicy.approvalMethod === "workflow" && (
                    <div>
                      <label className={labelCls}>Reminder days</label>
                      <input
                        type="number"
                        min={1}
                        className={inputCls}
                        value={newPolicy.reminderDays}
                        onChange={(e) =>
                          setNewPolicy((p) => ({ ...p, reminderDays: Number(e.target.value) || 7 }))
                        }
                      />
                    </div>
                  )}
                </div>
                {newPolicy.approvalMethod === "workflow" && (
                  <>
                <div className="flex gap-2 mb-2">
                  {[
                    { id: "resignation", label: "Resignation" },
                    { id: "termination", label: "Termination" },
                    { id: "cancellation", label: "Cancellation" },
                  ].map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setWorkflowStepsTab(t.id)}
                      className={`px-3 py-1 text-xs rounded-none border ${
                        workflowStepsTab === t.id
                          ? "rh-btn-primary border-redhalo-navy"
                          : "bg-white text-slate-700 border-slate-300"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                  <div className="space-y-2">
                    <p className="text-[10px] text-slate-500 mb-1">
                      Each row: role, SLA, notify-only, and optional notice waiver (max days) for this approver.
                    </p>
                    {(newPolicy[wfKey] || []).map((step, index) => (
                      <div
                        key={index}
                        className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-12 gap-2 p-2 border rounded-none items-end ${
                          darkMode ? "border-slate-600" : "border-slate-200"
                        }`}
                      >
                        <div className="lg:col-span-2">
                          <label className={`block text-[10px] mb-0.5 ${darkMode ? "text-slate-400" : "text-slate-600"}`}>
                            Role
                          </label>
                          <select
                            className={`${inputCls} py-1.5`}
                            value={step.role}
                            onChange={(e) => updateWorkflowStep(wfKey, index, "role", e.target.value)}
                          >
                            {WORKFLOW_ROLES.map((r) => (
                              <option key={r.value} value={r.value}>
                                {r.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="lg:col-span-1">
                          <label className={`block text-[10px] mb-0.5 ${darkMode ? "text-slate-400" : "text-slate-600"}`}>
                            SLA days
                          </label>
                          <input
                            type="number"
                            className={`${inputCls} py-1.5 w-full min-w-0`}
                            min={1}
                            value={step.slaDays}
                            onChange={(e) =>
                              updateWorkflowStep(wfKey, index, "slaDays", Number(e.target.value) || 3)
                            }
                          />
                        </div>
                        <div className="lg:col-span-2 flex items-end pb-1">
                          <label className="flex items-center text-[11px]">
                            <input
                              type="checkbox"
                              className="mr-1"
                              checked={!!step.notifyOnly}
                              onChange={(e) =>
                                updateWorkflowStep(wfKey, index, "notifyOnly", e.target.checked)
                              }
                            />
                            Notify only
                          </label>
                        </div>
                        <div className="lg:col-span-2 flex items-end pb-1">
                          <label className="flex items-center text-[11px]">
                            <input
                              type="checkbox"
                              className="mr-1"
                              checked={!!step.noticeWaiverAllowed}
                              onChange={(e) =>
                                updateWorkflowStep(wfKey, index, "noticeWaiverAllowed", e.target.checked)
                              }
                            />
                            Notice waiver
                          </label>
                        </div>
                        <div className="lg:col-span-2">
                          <label className={`block text-[10px] mb-0.5 ${darkMode ? "text-slate-400" : "text-slate-600"}`}>
                            Max waiver (days)
                          </label>
                          <input
                            type="number"
                            min={0}
                            disabled={!step.noticeWaiverAllowed}
                            className={`${inputCls} py-1.5 w-full min-w-0 ${!step.noticeWaiverAllowed ? "opacity-50" : ""}`}
                            value={step.noticeWaiverMaxDays ?? 0}
                            onChange={(e) =>
                              updateWorkflowStep(
                                wfKey,
                                index,
                                "noticeWaiverMaxDays",
                                Number(e.target.value) || 0
                              )
                            }
                          />
                        </div>
                        <div className="lg:col-span-2 flex items-end">
                          <button
                            type="button"
                            className="text-xs text-red-600 px-2 py-2 rounded-none bg-red-50 hover:bg-red-100"
                            onClick={() => removeWorkflowStep(wfKey, index)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="text-xs text-redhalo-navy"
                      onClick={() => addWorkflowStep(wfKey)}
                    >
                      + Add step
                    </button>
                  </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  <label className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      className="mr-1"
                      checked={!!newPolicy.workflowParallelApproval}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, workflowParallelApproval: e.target.checked }))
                      }
                    />
                    Parallel approval
                  </label>
                  <label className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      className="mr-1"
                      checked={!!newPolicy.workflowAutoApprovalIfNoAction}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, workflowAutoApprovalIfNoAction: e.target.checked }))
                      }
                    />
                    Auto-approve if no action
                  </label>
                  <label className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      className="mr-1"
                      checked={newPolicy.workflowRejectionWithReason !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, workflowRejectionWithReason: e.target.checked }))
                      }
                    />
                    Rejection with reason
                  </label>
                  <label className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      className="mr-1"
                      checked={newPolicy.workflowReinitiationAllowed !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, workflowReinitiationAllowed: e.target.checked }))
                      }
                    />
                    Re-initiation allowed
                  </label>
                </div>
                <div className="mt-2">
                  <label className={labelCls}>Escalation after (days)</label>
                  <input
                    type="number"
                    min={1}
                    className={`${inputCls} w-28`}
                    value={newPolicy.workflowEscalationAfterDays}
                    onChange={(e) =>
                      setNewPolicy((p) => ({
                        ...p,
                        workflowEscalationAfterDays: Number(e.target.value) || 3,
                      }))
                    }
                  />
                </div>
                  </>
                )}
              </div>

              <div className={sectionCls}>
                <h4 className="text-xs font-semibold uppercase tracking-wide mb-1 text-slate-700">
                  Checklist mapping
                </h4>
                <p className={`text-[11px] mb-2 ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                  One row per task. <strong>Depends on</strong> blocks this task until the selected task is completed.
                </p>
                <div className="flex justify-end mb-2">
                  <button
                    type="button"
                    onClick={addChecklistItem}
                    className="px-3 py-2 text-sm font-medium text-redhalo-navy hover:bg-redhalo-mint/40 border border-redhalo-teal/50 rounded-none"
                  >
                    + Add checklist item
                  </button>
                </div>
                <div className="space-y-2 overflow-x-auto">
                  {(newPolicy.checklistMappings || []).map((item, index) => {
                    const peerTasks = (newPolicy.checklistMappings || []).filter(
                      (t, i) => i !== index && t.taskId
                    );
                    return (
                      <div
                        key={item.taskId || `chk-${index}`}
                        className={`grid grid-cols-1 min-w-[880px] md:grid-cols-12 gap-2 p-2 border rounded-none items-end ${
                          darkMode ? "border-gray-600" : "border-gray-200"
                        }`}
                      >
                        <div className="md:col-span-2">
                          <label className={`block text-[10px] mb-0.5 ${darkMode ? "text-slate-400" : "text-slate-600"}`}>
                            Trigger
                          </label>
                          <select
                            className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-gray-800 border-gray-500 text-white" : "bg-white border-gray-300"}`}
                            value={item.triggerEvent}
                            onChange={(e) => updateChecklistItem(index, "triggerEvent", e.target.value)}
                          >
                            {CHECKLIST_TRIGGER_EVENTS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-1">
                          <label className={`block text-[10px] mb-0.5 ${darkMode ? "text-slate-400" : "text-slate-600"}`}>
                            Dept
                          </label>
                          <select
                            className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-gray-800 border-gray-500 text-white" : "bg-white border-gray-300"}`}
                            value={item.department}
                            onChange={(e) => updateChecklistItem(index, "department", e.target.value)}
                          >
                            {CHECKLIST_DEPARTMENTS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className={`block text-[10px] mb-0.5 ${darkMode ? "text-slate-400" : "text-slate-600"}`}>
                            Item name
                          </label>
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => updateChecklistItem(index, "name", e.target.value)}
                            placeholder="e.g. IT clearance"
                            className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-gray-800 border-gray-500 text-white" : "bg-white border-gray-300"}`}
                          />
                        </div>
                        <div className="md:col-span-1">
                          <label className={`block text-[10px] mb-0.5 ${darkMode ? "text-slate-400" : "text-slate-600"}`}>
                            Action role
                          </label>
                          <select
                            className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-gray-800 border-gray-500 text-white" : "bg-white border-gray-300"}`}
                            value={item.actionRole || "hr"}
                            onChange={(e) => updateChecklistItem(index, "actionRole", e.target.value)}
                          >
                            {CHECKLIST_ACTION_ROLES.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-1">
                          <label className={`block text-[10px] mb-0.5 ${darkMode ? "text-slate-400" : "text-slate-600"}`}>
                            Mandatory
                          </label>
                          <select
                            className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-gray-800 border-gray-500 text-white" : "bg-white border-gray-300"}`}
                            value={item.mandatory ? "yes" : "no"}
                            onChange={(e) =>
                              updateChecklistItem(index, "mandatory", e.target.value === "yes")
                            }
                          >
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </div>
                        <div className="md:col-span-1">
                          <label className={`block text-[10px] mb-0.5 ${darkMode ? "text-slate-400" : "text-slate-600"}`}>
                            Due logic
                          </label>
                          <select
                            className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-gray-800 border-gray-500 text-white" : "bg-white border-gray-300"}`}
                            value={item.dueDateLogic}
                            onChange={(e) => updateChecklistItem(index, "dueDateLogic", e.target.value)}
                          >
                            {DUE_DATE_LOGIC_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-1">
                          <label className={`block text-[10px] mb-0.5 ${darkMode ? "text-slate-400" : "text-slate-600"}`}>
                            Due days
                          </label>
                          <input
                            type="number"
                            min={0}
                            value={item.dueDateDays ?? 7}
                            onChange={(e) =>
                              updateChecklistItem(index, "dueDateDays", Number(e.target.value) || 0)
                            }
                            className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-gray-800 border-gray-500 text-white" : "bg-white border-gray-300"}`}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className={`block text-[10px] mb-0.5 ${darkMode ? "text-slate-400" : "text-slate-600"}`}>
                            Depends on (task)
                          </label>
                          <select
                            className={`w-full p-2 border rounded-none text-sm ${darkMode ? "bg-gray-800 border-gray-500 text-white" : "bg-white border-gray-300"}`}
                            value={item.dependsOnTaskId || ""}
                            onChange={(e) =>
                              updateChecklistItem(index, "dependsOnTaskId", e.target.value)
                            }
                          >
                            <option value="">— None —</option>
                            {peerTasks.map((t) => (
                              <option key={t.taskId} value={t.taskId}>
                                {t.name?.trim() || `Task ${t.taskId?.slice(0, 8)}…`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-1 flex items-end">
                          <button
                            type="button"
                            onClick={() => removeChecklistItem(index)}
                            className="w-full px-2 py-2 text-sm rounded-none bg-red-100 text-red-700 hover:bg-red-200"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {(newPolicy.checklistMappings || []).length === 0 && (
                  <p className={`text-xs mt-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                    Add clearance tasks (IT, Finance, Admin, assets, exit interview, etc.).
                  </p>
                )}
              </div>

              <div className={sectionCls}>
                <h4 className="text-xs font-semibold uppercase tracking-wide mb-2 text-slate-700">Applicability</h4>
                <label className="flex items-center text-xs mb-2">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={!!newPolicy.allEmployees}
                    onChange={(e) => setNewPolicy((p) => ({ ...p, allEmployees: e.target.checked }))}
                  />
                  All employees
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {[
                    ["branch", branchesList, "Branch"],
                    ["department", departmentsList, "Department"],
                    ["designation", designationsList, "Designation"],
                    ["grade", gradesList, "Grade"],
                    ["category", categoriesList, "Category"],
                    ["subDepartment", subDepartmentsList, "Sub-dept"],
                  ].map(([key, list, label]) => (
                    <div key={key}>
                      <label className={labelCls}>{label}</label>
                      <select
                        disabled={newPolicy.allEmployees}
                        className={inputCls}
                        value={newPolicy[key] || ""}
                        onChange={(e) => setNewPolicy((p) => ({ ...p, [key]: e.target.value }))}
                      >
                        <option value="">All</option>
                        {(list || []).map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                  <div>
                    <label className={labelCls}>Location</label>
                    <select
                      disabled={newPolicy.allEmployees}
                      className={inputCls}
                      value={newPolicy.location || ""}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, location: e.target.value }))}
                    >
                      {LOCATION_OPTIONS.map((loc) => (
                        <option key={loc.value || "all"} value={loc.value}>
                          {loc.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-3">
                  <label className={labelCls}>Employment types (empty = all)</label>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {EMPLOYMENT_TYPE_OPTIONS.map((opt) => (
                      <label key={opt.value} className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          className="mr-2"
                          disabled={newPolicy.allEmployees}
                          checked={
                            Array.isArray(newPolicy.employmentTypes) &&
                            newPolicy.employmentTypes.includes(opt.value)
                          }
                          onChange={() => toggleEmploymentType(opt.value)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="mt-3">
                  <label className={labelCls}>Individual employee IDs (comma or space separated)</label>
                  <input
                    className={inputCls}
                    disabled={newPolicy.allEmployees}
                    value={newPolicy.individualEmployeeIds || ""}
                    onChange={(e) =>
                      setNewPolicy((p) => ({ ...p, individualEmployeeIds: e.target.value }))
                    }
                    placeholder="e.g. 101 102"
                  />
                </div>
                {loadingAllEmployees && (
                  <p className="text-[10px] text-slate-500 mt-1">Loading employee directory for linkage checks…</p>
                )}
              </div>
            </div>

            <div
              className={`px-5 py-3 border-t flex justify-end gap-2 flex-shrink-0 flex-wrap ${
                darkMode ? "border-slate-600 bg-slate-900/80" : "border-slate-200 bg-white"
              }`}
            >
              {policySaveError && <p className="text-sm text-red-600 mr-auto">{policySaveError}</p>}
              <button
                type="button"
                onClick={() => setFlowchartPolicy(newPolicy)}
                className="px-4 py-2 text-sm rounded-lg bg-violet-600 text-white hover:bg-violet-700"
              >
                Preview flowchart
              </button>
              <button
                type="button"
                onClick={handleCancelPolicy}
                className={`px-4 py-2 text-sm rounded-lg ${
                  darkMode ? "bg-slate-700 text-white" : "bg-slate-100 text-slate-800"
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => requestSavePolicyForm()}
                className="px-4 py-2 text-sm rounded-lg rh-btn-primary hover:opacity-95"
              >
                Preview &amp; Save Policy
              </button>
            </div>
          </div>
        </div>
      )}

      {saveGateModal?.type === "effective_date" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10005] p-4">
          <div className="w-[min(100%,480px)] bg-white border border-amber-300 shadow-xl rounded-none p-6">
            <h3 className="text-lg font-semibold text-amber-900">Effective date must be updated</h3>
            <p className="text-sm text-slate-700 mt-3">
              Policy content changed but the effective date is unchanged. Set a new effective date (often future) before
              saving.
            </p>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                className="px-4 py-2 text-sm bg-amber-700 text-white"
                onClick={() => setSaveGateModal(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {saveGateModal?.type === "overlap" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10005] p-4">
          <div className="w-[min(100%,560px)] max-h-[85vh] bg-white border shadow-xl flex flex-col rounded-none">
            <div className="px-5 py-4 border-b bg-amber-50">
              <h3 className="text-base font-semibold">Applicability overlap</h3>
              <p className="text-sm text-slate-700 mt-2">
                Another active policy may cover the same employees. You can still save after confirming.
              </p>
            </div>
            <div className="px-5 py-3 overflow-y-auto flex-1">
              <ul className="space-y-2 text-sm">
                {saveGateModal.items.map((item) => (
                  <li key={item.id} className="border p-2 bg-slate-50">
                    <p className="font-semibold">{item.name}</p>
                    <p className="text-xs text-slate-600">{item.detail}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-5 py-4 border-t flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 text-sm border"
                onClick={() => setSaveGateModal(null)}
              >
                Back
              </button>
              <button type="button" className="px-4 py-2 text-sm rh-btn-primary" onClick={continueAfterOverlapAck}>
                Continue to preview &amp; save
              </button>
            </div>
          </div>
        </div>
      )}

      {showSummaryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4">
          <div className="w-[780px] max-w-[95vw] max-h-[85vh] bg-white shadow-xl flex flex-col">
            <div className="px-4 py-3 border-b flex justify-between items-center">
              <h3 className="font-semibold">Policy preview</h3>
              <button type="button" onClick={() => setShowSummaryModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="p-4 overflow-auto text-sm">
              {getPolicySummaryRows(newPolicy).map((row, idx) => (
                <div key={idx} className="grid grid-cols-3 gap-2 border-b py-2">
                  <span className="font-medium">{row.label}</span>
                  <span className="col-span-2">{String(row.value ?? "—")}</span>
                </div>
              ))}
              <p className="mt-3 text-slate-600">
                <strong>Roles that can approve:</strong> {selectedApprovalRoleLabels.join(", ") || "—"}
              </p>
            </div>
            <div className="px-4 py-3 border-t flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200"
                onClick={() => setShowSummaryModal(false)}
              >
                Back
              </button>
              <button type="button" className="px-4 py-2 rh-btn-primary" onClick={handleSavePolicy}>
                Save policy
              </button>
            </div>
          </div>
        </div>
      )}

      {previewPolicy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4">
          <div className="w-[780px] max-w-[95vw] max-h-[85vh] bg-white shadow-xl flex flex-col">
            <div className="px-4 py-3 border-b flex justify-between">
              <h3 className="font-semibold">Saved policy preview</h3>
              <button type="button" onClick={() => setPreviewPolicy(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="p-4 overflow-auto text-sm">
              {getPolicySummaryRows(normalizePolicyRow(previewPolicy)).map((row, idx) => (
                <div key={idx} className="grid grid-cols-3 gap-2 border-b py-2">
                  <span className="font-medium">{row.label}</span>
                  <span className="col-span-2">{String(row.value ?? "—")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {flowchartPolicy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4">
          <div className="w-[min(96vw,1120px)] max-h-[90vh] bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-slate-900 text-sm">
                  Preview flowchart — {flowchartPolicy.policyName || "Policy"}
                </h3>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Single end-to-end flow (print or Save as PDF from the dialog). Same layout style as confirmation
                  policy flowchart.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => printCombinedFlowchartPdf(flowchartPolicy)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-800 text-white hover:bg-slate-900"
                >
                  <Printer size={14} />
                  Print / Save as PDF
                </button>
                <button
                  type="button"
                  onClick={() => setFlowchartPolicy(null)}
                  className="text-slate-500 hover:text-slate-800 p-1"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-auto bg-white">
              <div
                className="w-full overflow-x-auto rounded-lg border border-slate-100 bg-slate-50/50 p-3"
                dangerouslySetInnerHTML={{ __html: buildSeparationFlowchartSvg(flowchartPolicy) }}
              />
            </div>
          </div>
        </div>
      )}

      {actionMenu &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[10018]" aria-hidden onClick={() => setActionMenu(null)} />
            <div
              className="fixed z-[10019] w-56 rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg dark:border-[#3C3C4C] dark:bg-[#1D1D1D]"
              style={{ top: actionMenu.top, left: actionMenu.left }}
              role="menu"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  openEditForm(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Edit
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  setPreviewPolicy(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Preview (configuration)
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  openPolicyDocumentPreview(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Policy document (HR)
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  setFlowchartPolicy(normalizePolicyRow(actionMenu.policy));
                  setActionMenu(null);
                }}
              >
                Flowchart
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  printPolicyDocument(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Print (configuration summary)
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-redhalo-navy-dark hover:bg-gray-50 dark:text-redhalo-mint dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  if (window.confirm("Publish policy document to assigned employees?")) {
                    handlePushPolicyDocument(actionMenu.policy);
                  }
                }}
              >
                Push policy document
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  handleCopyPolicy(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Copy
              </button>
              {actionMenu.policy.status === "active" ? (
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-amber-800 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-[#3C3C3C]"
                  onClick={() => {
                    handleDeactivatePolicy(actionMenu.policy.id);
                    setActionMenu(null);
                  }}
                >
                  Deactivate
                </button>
              ) : (
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-emerald-800 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-[#3C3C3C]"
                  onClick={() => {
                    handleReactivatePolicy(actionMenu.policy.id);
                    setActionMenu(null);
                  }}
                >
                  Reactivate
                </button>
              )}
              <button
                type="button"
                className="w-full border-t border-gray-200 px-3 py-2 text-left text-redhalo-red hover:bg-rose-50 dark:border-[#3C3C4C] dark:text-rose-400 dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  handleDeletePolicy(actionMenu.policy.id);
                  setActionMenu(null);
                }}
              >
                Delete
              </button>
            </div>
          </>,
          document.body
        )}

      {policyDocModal &&
        (() => {
          const docPolicy = normalizePolicyRow(
            policies.find((p) => p.id === policyDocModal.id) || policyDocModal
          );
          const docHtml = buildSeparationPolicyDocumentHtml(docPolicy, policyDocumentOptions);
          return (
            <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-black/50">
              <div className="w-[min(96vw,880px)] max-h-[92vh] bg-white flex flex-col border border-slate-300 rounded-none shadow-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 bg-slate-50">
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">Policy document</h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {docCompanyName}
                      {docCompanyAddress ? ` · ${docCompanyAddress.split("\n")[0]}` : ""}
                    </p>
                    <p className="text-xs text-slate-600 mt-1 font-medium">{docPolicy.policyName || docPolicy.name}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Document v{docPolicy.policyVersion ?? 1} · Effective {docPolicy.effectiveDate || "—"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPolicyDocModal(null)}
                    className="text-slate-500 hover:text-slate-800 p-1"
                    aria-label="Close"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="flex-1 min-h-0 overflow-hidden bg-slate-100 p-2">
                  <iframe title="Policy document" className="w-full h-[min(70vh,640px)] border border-slate-200 bg-white" srcDoc={docHtml} />
                </div>
                <div className="px-4 py-3 border-t flex flex-wrap gap-2 justify-between bg-white">
                  <button
                    type="button"
                    className="text-xs border px-3 py-1.5"
                    onClick={() => setPolicyHistoryModalId(docPolicy.id)}
                  >
                    View history
                  </button>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border"
                      onClick={() => printPolicyFullDocument(docPolicy)}
                    >
                      <Printer size={14} /> Print document
                    </button>
                    <button type="button" className="px-3 py-1.5 text-xs border" onClick={() => setPolicyDocModal(null)}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      {policyHistoryModalId &&
        (() => {
          const rawHist = policies.find((p) => p.id === policyHistoryModalId);
          if (!rawHist) return null;
          const histPolicy = normalizePolicyRow(rawHist);
          const rows = getPolicySnapshotsForDisplay(histPolicy);
          const previewHtml = historyPreviewEntry
            ? buildSeparationPolicyDocumentHtml(historyPreviewEntry.policy, policyDocumentOptions)
            : "";
          return (
            <div className="fixed inset-0 z-[10003] flex items-center justify-center p-4 bg-black/55">
              <div className="w-[min(96vw,900px)] max-h-[92vh] bg-white border shadow-xl flex flex-col overflow-hidden">
                <div className="px-4 py-3 border-b flex justify-between bg-slate-50">
                  <h3 className="font-semibold text-sm">Policy version history</h3>
                  <button type="button" onClick={() => setPolicyHistoryModalId(null)}>
                    <X size={18} />
                  </button>
                </div>
                <div className="flex-1 overflow-auto p-4">
                  <table className="w-full text-left text-xs border border-collapse">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="px-3 py-2">Version</th>
                        <th className="px-3 py-2">Effective</th>
                        <th className="px-3 py-2">Recorded</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={`${row.version}-${row.savedAt}`} className="border-b">
                          <td className="px-3 py-2">{row.version}</td>
                          <td className="px-3 py-2">{row.effectiveDate || "—"}</td>
                          <td className="px-3 py-2">
                            {row.savedAt ? new Date(row.savedAt).toLocaleString() : "—"}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              className="text-redhalo-navy mr-3"
                              onClick={() => setHistoryPreviewEntry(row)}
                            >
                              View
                            </button>
                            <button
                              type="button"
                              onClick={() => printPolicySnapshotDocument(row.policy)}
                            >
                              Print
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {historyPreviewEntry && (
                    <div className="mt-4 border p-2 bg-slate-50">
                      <p className="text-xs font-semibold mb-2">
                        Preview v{historyPreviewEntry.version}
                      </p>
                      <iframe title="Version preview" className="w-full h-[min(50vh,480px)] border bg-white" srcDoc={previewHtml} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default SeparationPolicyComponent;
